import { Moment } from 'moment';
import {
 CalendarEvent, EEventTypes, EEventStatus, BirthdayEvent, AnniversaryEvent,
 EEventFilterTypes,
 EApiResponseTypes
} from '../types/events';
import Api from '@common/services/api';

interface APICalendarEvent {
  type: EApiResponseTypes;
  event_type: EEventTypes;
  is_full_day_event: boolean;
  date: string;
  start_time: string | null;
  end_time: string | null;
  event_data: any;
}

function transformAPIEventToCalendarEvent(apiEvent: APICalendarEvent, loggedUserId?: string, t?: any): CalendarEvent[] {
  const events: CalendarEvent[] = [];

  switch (apiEvent.event_type) {
    case EEventTypes.EXCHANGE:
      const functionName = apiEvent.event_data.functions?.[0]?.name || '';
      const functionLength = apiEvent.event_data.functions?.length;
      const functionCount = typeof functionLength === 'number' ? functionLength - 2 : 0;
      const filterTypes = [];
      if (apiEvent.event_data.exchange_status === EEventStatus.OPEN
        || apiEvent.event_data.exchange_status === EEventStatus.PENDING) {
        filterTypes.push(EEventFilterTypes.OPEN_EXCHANGE);
      }
      if (loggedUserId === apiEvent.event_data.user_id) {
        filterTypes.push(EEventFilterTypes.MY_EXCHANGE);
      }
      if (apiEvent.event_data.exchange_status === EEventStatus.PENDING) {
        filterTypes.push(EEventFilterTypes.PENDING_EXCHANGE);
      }
      if (apiEvent.event_data.is_approved && apiEvent.event_data.approved_by_id === loggedUserId) {
        filterTypes.push(EEventFilterTypes.APPROVED_EXCHANGE);
      }
      events.push({
        id: apiEvent.event_data.id,
        type: EEventTypes.EXCHANGE,
        filter_type: filterTypes,
        date: apiEvent.date,
        start_time: apiEvent.start_time,
        end_time: apiEvent.end_time,
        description: apiEvent.event_data.description,
        user_id: apiEvent.event_data.user_id,
        user: apiEvent.event_data.user,
        status: apiEvent.event_data.is_approved ? EEventStatus.APPROVED :
                apiEvent.event_data.response_status === EEventStatus.APPROVED ? EEventStatus.ACCEPTED :
                apiEvent.event_data.response_status === EEventStatus.DECLINED ? EEventStatus.REJECTED :
                apiEvent.event_data.exchange_status === EEventStatus.APPROVED ? EEventStatus.APPROVED :
                apiEvent.event_data.exchange_status === EEventStatus.PENDING ? EEventStatus.AWAITING_APPROVAL :
                apiEvent.event_data.exchange_status === EEventStatus.EXPIRED ? EEventStatus.EXPIRED :
                EEventStatus.OPEN,
        created_for: apiEvent.event_data.network?.name || '',
        function_name: functionName ? functionName + (functionCount > 0 ? ` + ${functionCount}` : '') : '',
        created_in: { values: apiEvent.event_data.functions?.map((f: any) => f.id) || [] },
        created_at: apiEvent.event_data.created_at
      } as CalendarEvent);
      break;

    case EEventTypes.BIRTHDAY:
      if (Array.isArray(apiEvent.event_data) && apiEvent.event_data.length > 0) {
        events.push({
          id: `birthday-${apiEvent.date}`,
          type: EEventTypes.BIRTHDAY,
          filter_type: EEventFilterTypes.CELEBRATION,
          title:
            apiEvent.event_data.length === 1
            ? `${t('calendar:birthday')} ${apiEvent.event_data[0].first_name}`
              : `${apiEvent.event_data.length} ${t('social:birthdays')}`,
          date: apiEvent.date,
          description: `${t('social:birthdays')} ${apiEvent.date}`,
          users: apiEvent.event_data,
          created_at: apiEvent.date,
        } as BirthdayEvent);
      }
      break;

    case EEventTypes.ANNIVERSARY:
      if (Array.isArray(apiEvent.event_data)) {
        events.push({
          id: `anniversary-${apiEvent.date}`,
          type: EEventTypes.ANNIVERSARY,
          filter_type: EEventFilterTypes.CELEBRATION,
          title:
            apiEvent.event_data.length === 1
              ? `${t('calendar:anniversary')} ${apiEvent.event_data[0].first_name}`
              : `${apiEvent.event_data.length} ${t('social:anniversaries')}`,
          date: apiEvent.date,
          description: `${t('social:anniversaries')} ${apiEvent.date}`,
          users: apiEvent.event_data,
          years: 1, // optional
          started_at: apiEvent.date,
          created_at: apiEvent.date,
        } as AnniversaryEvent);
      }
      break;


    case EEventTypes.SHIFT:
      events.push({
        id: apiEvent.event_data.id || apiEvent.date,
        type: EEventTypes.SHIFT,
        filter_type: EEventFilterTypes.SHIFT,
        title: apiEvent.event_data.title || '',
        date: apiEvent.date,
        start_time: apiEvent.start_time,
        end_time: apiEvent.end_time,
        description: apiEvent.event_data.description,
        user: apiEvent.event_data.user,
        network_name: apiEvent.event_data.network?.name || apiEvent.event_data.external_network_name || '',
        function_name: apiEvent.event_data.function?.name || apiEvent.event_data.external_function_name || '',
        function_id: apiEvent.event_data.function?.id || '',
        team_id: apiEvent.event_data.team?.id || '',
        exchange_id: apiEvent.event_data.exchange_id || '',
        team: apiEvent.event_data.team,
        function: apiEvent.event_data.function,
        break: apiEvent.event_data.break,
        location: apiEvent.event_data.location,
        is_integrated: true,
        created_at: apiEvent.event_data.created_at || apiEvent.date
      } as CalendarEvent);
      break;

    case EEventTypes.EVENT:
      events.push({
        id: apiEvent.event_data.id,
        type: EEventTypes.EVENT,
        filter_type: EEventFilterTypes.EVENT,
        title: apiEvent.event_data.name,
        date: apiEvent.date,
        start_time: apiEvent.start_time,
        end_time: apiEvent.end_time,
        description: apiEvent.event_data.description,
        location: apiEvent.event_data.location,
        attending_users_count: apiEvent.event_data.going_users?.count || 0,
        attendees: apiEvent.event_data.going_users?.users_preview || [],
        event_category: 'meeting',
        is_mandatory: false,
        status: apiEvent.event_data.status,
        is_full_day_event: apiEvent.is_full_day_event,
        is_going: apiEvent.event_data.is_going,
        settings: apiEvent.event_data.settings,
        created_at: apiEvent.event_data.created_at
      } as CalendarEvent);
      break;

    default:
      break;
  }

  return events;
}

export async function fetchEventsFromAPI(week: Moment, organisationId: string, loggedUserId?: string, t?: any):
Promise<CalendarEvent[]> {
  const startTime = week.clone().startOf('isoWeek').startOf('day').utc()
  .toISOString();
  const endTime = week.clone().endOf('isoWeek').endOf('day').utc()
  .toISOString();

  try {
    const response = await Api.get(
      `/v2/organisations/${organisationId}/calendar/events?start_time=${startTime}&end_time=${endTime}`
    ) as any;

    let apiEvents = response.data?.events || response.data || response.events || response || [];

    if (!Array.isArray(apiEvents) && apiEvents.type) {
      apiEvents = [apiEvents];
    }

    const transformedEvents: CalendarEvent[] = [];

    if (Array.isArray(apiEvents)) {
      apiEvents.forEach((apiEvent: APICalendarEvent) => {
        const events = transformAPIEventToCalendarEvent(apiEvent, loggedUserId, t);
        transformedEvents.push(...events);
      });
    }

    return transformedEvents;
  } catch (error) {
    console.error('Error fetching calendar events:', error);
    throw error;
  }
}

export async function fetchCalendarEvents(week: Moment, organisationId?: string, loggedUserId?: string, t?: any):
 Promise<CalendarEvent[]> {

  if (organisationId) {
      return fetchEventsFromAPI(week, organisationId, loggedUserId, t);
  }
  return [];
}

